// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Relatorios/BudgetVsRealizado.tsx
//
//  Fase 3 · Iter 3.4 — Comparativo Budget × Realizado por mês e por grupo.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  AlertTriangle, CheckCircle2, DollarSign, Target, TrendingUp,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import { useBudgetVsRealizado } from '@/features/compras/hooks/useBudget';
import type { BudgetLinha, BudgetStatus } from '@/features/compras/types';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v: number) => `${v.toFixed(1)}%`;

const MESES = [
  { v: 1,  n: 'Jan' }, { v: 2,  n: 'Fev' }, { v: 3,  n: 'Mar' },
  { v: 4,  n: 'Abr' }, { v: 5,  n: 'Mai' }, { v: 6,  n: 'Jun' },
  { v: 7,  n: 'Jul' }, { v: 8,  n: 'Ago' }, { v: 9,  n: 'Set' },
  { v: 10, n: 'Out' }, { v: 11, n: 'Nov' }, { v: 12, n: 'Dez' },
];

const statusColor = (s: BudgetStatus) =>
  s === 'DentroDoPlanejado' ? 'bg-green-500/10 text-green-700 border-green-500/30'
 : s === 'Atencao'           ? 'bg-amber-500/10 text-amber-700 border-amber-500/30'
 : s === 'Estourado'         ? 'bg-destructive/10 text-destructive border-destructive/30'
 :                             'bg-muted text-muted-foreground';

const statusLabel = (s: BudgetStatus) =>
  s === 'DentroDoPlanejado' ? 'No plano'
 : s === 'Atencao'           ? 'Atenção'
 : s === 'Estourado'         ? 'Estourado'
 :                             'Sem orçamento';

const BudgetVsRealizado = () => {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [mesIni, setMesIni] = useState<number>(1);
  const [mesFim, setMesFim] = useState<number>(12);

  const { data, isLoading } = useBudgetVsRealizado(ano, mesIni, mesFim);
  const kpis   = data?.resumo;
  const serie  = data?.seriePorMes ?? [];
  const linhas = data?.linhasPorGrupo ?? [];

  const chart = serie.map((s) => ({
    mes:       s.rotuloMes,
    planejado: s.valorPlanejado,
    realizado: s.valorRealizado,
  }));

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Budget × Realizado · Compras</h1>
        <p className="text-muted-foreground mt-1">
          Compara o orçamento definido com os gastos reais por mês e por categoria de produto.
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Ano</Label>
            <Input
              type="number"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value) || anoAtual)}
              min={2000} max={2100}
            />
          </div>
          <div className="space-y-1">
            <Label>Mês início</Label>
            <Select value={String(mesIni)} onValueChange={(v) => setMesIni(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESES.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Mês fim</Label>
            <Select value={String(mesFim)} onValueChange={(v) => setMesFim(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESES.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {isLoading || !kpis ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <KpiCard
            icon={<Target className="h-4 w-4 text-blue-500" />}
            label="Planejado"
            valor={fmt(kpis.totalPlanejado)}
            subtitulo={`${kpis.qtdGruposOrcados} grupo${kpis.qtdGruposOrcados === 1 ? '' : 's'}`}
          />
          <KpiCard
            icon={<DollarSign className="h-4 w-4 text-amber-500" />}
            label="Realizado"
            valor={fmt(kpis.totalRealizado)}
            subtitulo={fmtPct(kpis.percentualExecutado) + ' do plano'}
          />
          <KpiCard
            icon={<CheckCircle2 className="h-4 w-4 text-green-500" />}
            label="Saldo"
            valor={fmt(kpis.saldoDisponivel)}
            destaque={kpis.saldoDisponivel < 0 ? 'destructive' : undefined}
          />
          <KpiCard
            icon={<AlertTriangle className="h-4 w-4 text-destructive" />}
            label="Grupos estourados"
            valor={String(kpis.qtdGruposEstourados)}
            destaque={kpis.qtdGruposEstourados > 0 ? 'destructive' : undefined}
          />
          <KpiCard
            icon={<TrendingUp className="h-4 w-4 text-purple-500" />}
            label="Execução"
            valor={fmtPct(kpis.percentualExecutado)}
          />
        </div>
      )}

      {/* Gráfico mensal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Planejado × Realizado por mês</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : chart.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Sem dados.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="planejado" name="Planejado" fill="#3b82f6" />
                <Bar dataKey="realizado" name="Realizado" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabela por grupo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detalhe por grupo de produto</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : linhas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados.</p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Grupo</TableHead>
                    <TableHead className="text-right">Planejado</TableHead>
                    <TableHead className="text-right">Realizado</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-right">% exec.</TableHead>
                    <TableHead>Barra</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linhas.map((l: BudgetLinha) => (
                    <TableRow key={l.grupoProdutoId}>
                      <TableCell className="text-sm max-w-[260px] truncate" title={l.grupoProdutoNome ?? ''}>
                        {l.grupoProdutoNome ?? `#${l.grupoProdutoId}`}
                      </TableCell>
                      <TableCell className="text-right">{fmt(l.valorPlanejado)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(l.valorRealizado)}</TableCell>
                      <TableCell className="text-right">
                        <span className={l.saldo < 0 ? 'text-destructive' : 'text-green-700'}>
                          {fmt(l.saldo)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {l.valorPlanejado > 0 ? fmtPct(l.percentualExecutado) : '—'}
                      </TableCell>
                      <TableCell className="min-w-[160px]">
                        {l.valorPlanejado > 0 ? (
                          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                            <div
                              className={
                                l.percentualExecutado > 100 ? 'bg-destructive h-2'
                              : l.percentualExecutado >= 75 ? 'bg-amber-500 h-2'
                              :                               'bg-green-500 h-2'
                              }
                              style={{ width: `${Math.min(100, l.percentualExecutado)}%` }}
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusColor(l.status)}>
                          {statusLabel(l.status)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BudgetVsRealizado;

// ── KpiCard ──────────────────────────────────────────────────────────────────

function KpiCard({
  icon, label, valor, subtitulo, destaque,
}: {
  icon:       React.ReactNode;
  label:      string;
  valor:      string;
  subtitulo?: string;
  destaque?:  'destructive';
}) {
  const borda = destaque === 'destructive' ? 'border-destructive/50' : '';
  return (
    <Card className={borda}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs text-muted-foreground flex items-center gap-2 font-normal">
          {icon} {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-bold">{valor}</div>
        {subtitulo && <p className="text-xs text-muted-foreground mt-1">{subtitulo}</p>}
      </CardContent>
    </Card>
  );
}
